import { useMemo, useState } from 'react'

import {
  errorView,
  queryView,
  resourceTag,
  useWindow,
  useDebouncedValue,
  useQueryResult,
} from '@krp/client-runtime'
import {
  ALL_PRICE_TYPES,
  EMPTY_PRICE_DRAFT,
  ONEC_PRICING_TAG,
  PriceListScreen,
  draftProblem,
  makeSelectPriceListVm,
  onecConnectionForm,
  onecCreatePriceRecord,
  onecDeletePriceRecord,
  onecPriceList,
  onecPriceTypes,
  onecProductSearch,
  parsePrice,
  priceListArgs,
  priceListIntentOf,
  priceListPageChanged,
  priceListTypeChosen,
  pricingApi,
  type PriceDraft,
  type PriceListActions,
  type PriceManageActions,
  type PriceManageVm,
  type ProductSearchVm,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { SEARCH_DEBOUNCE_MS } from '../platform/timings'
import { useResourceRevision } from '../platform/useResourceRevision'

const MIN_PRODUCT_QUERY = 2
const PRODUCT_LIMIT = 10
const NO_REMOVAL = { confirmKey: null as string | null, productKey: '', priceTypeKey: '', period: '' }

/**
 * Binding окна «Прайс 1С» (RP-FB10): rid — из окна; вид цен, поиск и страница — намерение этой базы. Управление ценами как
 * в 1С (17.10.2026): форма «Создать цену» и «Удалить» у записи — только человеку с правом `prices.write` этой базы.
 * Черновик формы живёт, пока открыт экран; ответы 1С в нём не хранятся.
 */
export function PriceListBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  // Открыт из «Видов цен» (`:typeKey` в адресе) — цены ровно этого вида; иначе — выбор вида человека.
  const typeKey = window.params.typeKey ?? null
  const savedIntent = useAppSelector((state) => priceListIntentOf(state.onec.pricing, resourceId))
  const intent = useMemo(() => (typeKey === null ? savedIntent : { ...savedIntent, priceType: typeKey }), [savedIntent, typeKey])
  const types = useQueryResult(onecPriceTypes, useMemo(() => (resourceId === '' || typeKey === null ? null : { resourceId }), [resourceId, typeKey]))
  const typeName = typeKey === null ? undefined : (types.data?.data.items.find((item) => item.key === typeKey)?.name ?? 'вид цен')
  const args = useMemo(() => (resourceId === '' ? null : priceListArgs(resourceId, intent)), [intent, resourceId])
  const list = useQueryResult(onecPriceList, args)
  const selectVm = useMemo(() => makeSelectPriceListVm(), [])
  const vm = selectVm({ list, intent })
  const canWrite = useResourceRevision(resourceId, onecConnectionForm.kind, 'prices.write') !== null

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<PriceDraft>(EMPTY_PRICE_DRAFT)
  const [removal, setRemoval] = useState(NO_REMOVAL)
  const [created, createState] = onecCreatePriceRecord.useMutation({ fixedCacheKey: `${resourceId}:price-create` })
  const [deleted, deleteState] = onecDeletePriceRecord.useMutation({ fixedCacheKey: `${resourceId}:price-delete` })

  // Вид цен в форме по умолчанию — тот, что выбран в прайсе.
  const effectiveDraft = useMemo<PriceDraft>(
    () =>
      draft.priceType === '' && vm.priceType !== '' && vm.priceType !== ALL_PRICE_TYPES ? { ...draft, priceType: vm.priceType } : draft,
    [draft, vm.priceType],
  )

  const search = useDebouncedValue(effectiveDraft.productSearch.trim(), SEARCH_DEBOUNCE_MS)
  const searchArgs = useMemo(
    () =>
      !open || resourceId === '' || effectiveDraft.product !== null || search.length < MIN_PRODUCT_QUERY
        ? null
        : { resourceId, q: search, limit: PRODUCT_LIMIT },
    [effectiveDraft.product, open, resourceId, search],
  )
  const found = useQueryResult(onecProductSearch, searchArgs)

  const products = useMemo<ProductSearchVm>(() => {
    if (searchArgs === null) return { status: 'idle', items: [], message: null }
    const view = queryView(found, (read) => read.data.length === 0)
    if (view.status === 'ready' && found.data !== undefined)
      return {
        status: 'ready',
        items: found.data.data.map((item) => ({
          key: item.refKey,
          label: [item.name, item.sku === null ? null : `арт. ${item.sku}`].filter((part) => part !== null).join(' · '),
        })),
        message: null,
      }
    if (view.status === 'empty') return { status: 'empty', items: [], message: `По запросу «${search}» товаров не найдено` }
    if (view.status === 'loading') return { status: 'loading', items: [], message: null }
    return { status: 'error', items: [], message: view.error?.message ?? 'Поиск товара не удался' }
  }, [found, search, searchArgs])

  const manageVm = useMemo<PriceManageVm>(
    () => ({
      open,
      draft: effectiveDraft,
      problem: draftProblem(effectiveDraft),
      products,
      saving: createState.isLoading,
      error: createState.isError && createState.error !== undefined ? toErrorVm(createState.error) : null,
      done: createState.isSuccess ? (createState.data?.data === 'unchanged' ? 'В 1С уже стоит эта цена' : 'Цена записана в 1С') : null,
      remove: {
        confirmKey: removal.confirmKey,
        busy: deleteState.isLoading,
        error: deleteState.isError && deleteState.error !== undefined ? toErrorVm(deleteState.error) : null,
        done: deleteState.isSuccess ? (deleteState.data?.data === 'unchanged' ? 'Такой записи в 1С уже нет' : 'Запись цены удалена') : null,
      },
    }),
    [createState, deleteState, effectiveDraft, open, products, removal.confirmKey],
  )

  const manageActions = useMemo<PriceManageActions>(
    () => ({
      openForm: () => {
        createState.reset()
        setOpen(true)
      },
      closeForm: () => {
        setOpen(false)
        setDraft(EMPTY_PRICE_DRAFT)
      },
      productSearchChanged: (text) => setDraft({ ...effectiveDraft, productSearch: text }),
      productChosen: (product) => setDraft({ ...effectiveDraft, product, productSearch: '' }),
      productCleared: () => setDraft({ ...effectiveDraft, product: null }),
      priceTypeChanged: (key) => setDraft({ ...effectiveDraft, priceType: key }),
      priceChanged: (text) => setDraft({ ...effectiveDraft, priceText: text }),
      periodChanged: (day) => setDraft({ ...effectiveDraft, period: day.trim() }),
      save: () => {
        const price = parsePrice(effectiveDraft.priceText)
        if (draftProblem(effectiveDraft) !== null || price === null || effectiveDraft.product === null) return
        void created({
          resourceId,
          productKey: effectiveDraft.product.key,
          priceTypeKey: effectiveDraft.priceType,
          price,
          period: effectiveDraft.period === '' ? null : effectiveDraft.period,
        })
          .unwrap()
          .then(() => {
            setOpen(false)
            setDraft(EMPTY_PRICE_DRAFT)
          })
          .catch(() => undefined)
      },
      askRemove: (rowKey) => {
        deleteState.reset()
        const row = vm.rows.find((item) => item.key === rowKey)
        if (row === undefined) return
        setRemoval({ confirmKey: rowKey, productKey: row.productKey, priceTypeKey: row.priceTypeKey, period: row.period })
      },
      cancelRemove: () => setRemoval(NO_REMOVAL),
      confirmRemove: () => {
        if (removal.confirmKey === null) return
        void deleted({ resourceId, productKey: removal.productKey, priceTypeKey: removal.priceTypeKey, period: removal.period })
          .unwrap()
          .then(() => setRemoval(NO_REMOVAL))
          .catch(() => setRemoval(NO_REMOVAL))
      },
    }),
    [created, createState, deleted, deleteState, effectiveDraft, removal, resourceId, vm.rows],
  )

  const actions = useMemo<PriceListActions>(
    () => ({
      priceTypeChosen: (priceType) => dispatch(priceListTypeChosen({ resourceId, priceType })),
      setPage: (page) =>
        dispatch(priceListPageChanged({ resourceId, page: Math.min(Math.max(page, 0), vm.pageCount - 1) })),
      reload: () => dispatch(pricingApi.util.invalidateTags([resourceTag(ONEC_PRICING_TAG, resourceId, 'PRICE-LIST')])),
    }),
    [dispatch, resourceId, vm.pageCount],
  )
  return (
    <PriceListScreen
      vm={vm}
      actions={actions}
      manage={canWrite ? { vm: manageVm, actions: manageActions } : undefined}
      {...(typeName === undefined ? {} : { typeName })}
    />
  )
}

function toErrorVm(error: Parameters<typeof errorView>[0]) {
  const view = errorView(error)
  return { message: view.message, code: view.code, correlationId: view.correlationId }
}
